/**
 * Number and date formatting for the Population Health dashboard (PD-1774). The API sends
 * times to resolve and their changes in days (one decimal) and count changes in percent; a
 * null value renders "—", never 0.
 */

export const NO_CHANGE = '—'

const integer = new Intl.NumberFormat('en-US')

export const formatCount = (value: number): string => integer.format(value)

/** A number of days, rounded to whole days, e.g. "46 days" or "1 day". */
export const formatDayCount = (days: number): string => {
  const whole = Math.round(days)
  return `${integer.format(whole)} ${Math.abs(whole) === 1 ? 'day' : 'days'}`
}

/** A median time to resolve, e.g. "46 days", or "—" when nothing resolved. */
export const formatDays = (days: number | null): string =>
  days === null ? NO_CHANGE : formatDayCount(days)

const signed = (value: number) => (value > 0 ? `+${value}` : value < 0 ? `−${-value}` : '0')

/** A count's change against the comparison period, e.g. "+12.5%". */
export const formatChangePercent = (value: number | null): string =>
  value === null ? NO_CHANGE : `${signed(value)}%`

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * A calendar date ('YYYY-MM-DD', or the date part of an ISO instant) as "Sep 26, 2026". The API's
 * dates are UTC calendar days, so they are read as text — never through the local time zone.
 */
export function formatDay(date: string, { year = true }: { year?: boolean } = {}): string {
  const [y, m, d] = date.slice(0, 10).split('-').map(Number)
  const day = `${MONTHS[m - 1]} ${d}`
  return year ? `${day}, ${y}` : day
}

/** An inclusive date range, e.g. "Aug 28 – Sep 26, 2026" or "Dec 28, 2025 – Jan 26, 2026". */
export function formatDateRange({ start, end }: { start: string; end: string }): string {
  const sameYear = start.slice(0, 4) === end.slice(0, 4)
  if (start === end) return formatDay(start)
  return `${formatDay(start, { year: !sameYear })} – ${formatDay(end)}`
}

/** A trend slice's axis label, e.g. "Sep 1–5" or "Aug 28 – Sep 1". */
export function formatSlice({ start, end }: { start: string; end: string }): string {
  if (start === end) return formatDay(start, { year: false })
  const [, sm, sd] = start.split('-').map(Number)
  const [, em, ed] = end.split('-').map(Number)
  return sm === em
    ? `${MONTHS[sm - 1]} ${sd}–${ed}`
    : `${MONTHS[sm - 1]} ${sd} – ${MONTHS[em - 1]} ${ed}`
}

/** An ISO instant in the viewer's time zone, e.g. "Sep 26, 2026, 5:15 AM". */
export const formatInstant = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

export const initials = (name: string | null): string =>
  (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?'
